/**
 * Service to track cart item additions/removals in localStorage
 */

const STORAGE_KEY = 'add_to_cart_tracking'
const TTL_MS = 60 * 24 * 60 * 60 * 1000 // 60 days in milliseconds

interface CartTrackingItem {
  source: string
  lastUpdated: number
}

interface CartTrackingData {
  items: Record<string, CartTrackingItem>
}

/**
 * Get cart tracking data from localStorage with error handling
 * @returns CartTrackingData object, or a fresh object if corrupted/missing
 */
function getCartData(): CartTrackingData {
  try {
    const raw = localStorage.getItem(STORAGE_KEY) || ''
    const data = JSON.parse(raw)
    if (!data.items || typeof data.items !== 'object') throw new Error('Invalid structure')

    return data
  } catch {
    return { items: {} }
  }
}

/**
 * Track cart item additions in localStorage
 * @param itemIds - IDs of the added items
 * @param source - Source of the addition (e.g., "catalog", "product page", etc.)
 */
export function trackAddToCartInLocalStorage(itemIds: string[], source: string): void {
  const now = Date.now()
  const cartData = getCartData()

  // 1. Purge entries that are too old (> 60 days) or have invalid format
  Object.entries(cartData.items).forEach(([itemId, item]) => {
    if (typeof item.lastUpdated !== 'number' || now - item.lastUpdated > TTL_MS) {
      delete cartData.items[itemId]
    }
  })

  // 2. For each itemId, add or update
  itemIds.forEach((itemId) => {
    cartData.items[itemId] = { source, lastUpdated: now }
  })

  localStorage.setItem(STORAGE_KEY, JSON.stringify(cartData))
}

/**
 * Track cart item removal in localStorage
 * @param itemId - ID of the removed item
 */
export function trackRemoveFromCart(itemId: string): void {
  const cartData = getCartData()
  delete cartData.items[itemId]
  localStorage.setItem(STORAGE_KEY, JSON.stringify(cartData))
}

/**
 * Get item source from localStorage
 * @param itemId - ID of the item
 * @returns The source of the addition or null if not found
 */
export function getTrackingItemSource(itemId: string): string | null {
  const cartData = getCartData()

  return cartData.items[itemId]?.source || null
}

/**
 * Completely clear cart tracking from localStorage
 */
export function clearCartTracking(): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ items: {} }))
}
